import React, { useState, useEffect, useMemo, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import moment from "moment";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import Select from "react-select";
import { getKeywordOption } from "../utils/adrUtilsV3";
import { ADR_REVIEW } from "../../../_constants/actionConstants";

export default function AdrClarificationCommentRowV3(props) {
  const { comment, tabId, projectId, userIsReviewer, markAdrDirty } = props;

  const dispatch = useDispatch();

  const commentDate = useRef(moment(comment.commentDate).format("MM/DD/YYYY"));

  const [subject, setSubject] = useState(comment.subject || "");
  const [commentBody, setCommentBody] = useState(comment.comment || "");
  const [selectedKeywords, selectKeywords] = useState([]); // Initialize once projKeywordList is avail
  const [includeInCem4501, setIncludeInCem4501] = useState(
    comment.includeInCem4501 || false
  );

  const projKeywordList = useSelector(
    (state) => state.keywords.projKeywordLists[projectId] || null
  );

  const projKeywordOptions = useMemo(
    () => (projKeywordList || []).map((pkw) => getKeywordOption(pkw)),
    [projKeywordList]
  );

  useEffect(() => {
    commentDate.current = moment(comment.commentDate).format("MM/DD/YYYY");
    setSubject(comment.subject || "");
    setCommentBody(comment.comment || "");
    selectKeywords(
      comment.keywordIds
        ? (projKeywordOptions || []).filter((pko) =>
            (comment.keywordIds || []).includes(pko.value)
          )
        : []
    );
    setIncludeInCem4501(comment.includeInCem4501 || false);
  }, [comment, projKeywordOptions]);

  const handleClick_delete = (e) => {
    e.preventDefault();
    markAdrDirty();
    dispatch({
      type: ADR_REVIEW.CLARIFICATION_COMMENTS.DELETE,
      clientId: comment.clientId,
      tabId,
    });
  };

  const handleChange_check = (e) => {
    setIncludeInCem4501(e.target.checked);
    markAdrDirty();
    dispatch({
      type: ADR_REVIEW.CLARIFICATION_COMMENTS.UPDATE,
      ccFields: { includeInCem4501: e.target.checked },
      clientId: comment.clientId,
      tabId,
    });
  };

  const handleChange_kw = (selectedKeywords) => {
    selectKeywords(selectedKeywords);
    markAdrDirty();
    dispatch({
      type: ADR_REVIEW.CLARIFICATION_COMMENTS.KEYWORDS.UPDATE,
      keywordIds: (selectedKeywords || []).map((kwo) => kwo.value),
      clientId: comment.clientId,
      tabId,
    });
  };

  const handleBlur = (e) =>
    dispatch({
      type: ADR_REVIEW.CLARIFICATION_COMMENTS.UPDATE,
      ccFields: { [e.target.name]: e.target.value },
      clientId: comment.clientId,
      tabId,
    });

  return (
    <div className="card mb-2">
      <div className="card-body p-2">
        <div className="mt-2 review-comment-row">
          <div className="form-row px-2">
            <div className="col-md-11">
              <div className="form-row form-group mb-1">
                <label className="col-sm-2 col-form-label">
                  <b>Comment Date:</b>
                </label>
                <div className="col-sm-10 my-auto">
                  <span>{commentDate.current}</span>
                </div>
              </div>
              <div className="form-row form-group mb-2">
                <label className="col-sm-2 col-form-label" htmlFor="subject">
                  <b>Subject:</b>
                </label>
                <input
                  type="text"
                  id={`clarCom_subj_${comment.clientId}`}
                  name="subject"
                  className="form-control col-sm-10"
                  value={subject}
                  onChange={(e) => {
                    markAdrDirty();
                    setSubject(e.target.value);
                  }}
                  onBlur={handleBlur}
                  disabled={!userIsReviewer}
                />
              </div>
              <div className="form-row form-group mb-2">
                <label className="col-sm-2 col-form-label">
                  Keywords:
                </label>
                <div className="col-sm-10 px-0">
                  <Select
                    name={"keywordIds"}
                    id={`clarCom_kw_${comment.clientId}`}
                    value={selectedKeywords || []}
                    onChange={(selected) => handleChange_kw(selected)}
                    options={projKeywordOptions || []}
                    placeholder="Select or Type Keywords"
                    isMulti
                    isClearable
                    isSearchable
                    isDisabled={!userIsReviewer}
                    styles={{
                      // Fixes the overlapping problem of the component
                      menu: (provided) => ({ ...provided, zIndex: 9999 }),
                    }}
                  />
                </div>
              </div>
              <div className="form-row form-group mb-2">
                <label className="col-sm-2">
                  <b>Comments:</b>
                </label>
                <div className="col-sm-10 px-0">
                  <textarea
                    className="form-control"
                    rows="4"
                    name="comment"
                    id={`clarCom_${comment.clientId}`}
                    value={commentBody}
                    onChange={(e) => {
                      markAdrDirty();
                      setCommentBody(e.target.value);
                    }}
                    onBlur={handleBlur}
                    disabled={!userIsReviewer}
                  />
                </div>
              </div>
              <div className="form-row form-group mb-1">
                <div className="col-sm-2"></div>
                <div className="col-sm-10">
                  <div className="custom-control-inline custom-control custom-checkbox">
                    <input
                      type="checkbox"
                      name="includeInCem4501"
                      id={`includeInCem4501_${comment.clientId}`}
                      className="custom-control-input my-auto"
                      value={includeInCem4501}
                      checked={includeInCem4501}
                      onChange={handleChange_check}
                    />
                    <label
                      htmlFor={`includeInCem4501_${comment.clientId}`}
                      className="custom-control-label my-auto"
                    >
                      Include this comment in my RE/SR Daily Report (CEM-4501)
                    </label>
                  </div>
                </div>
              </div>
            </div>
            <div className="col-md-1 px-1">
              <div className="form-row form-group float-right">
                <button
                  type="button"
                  name="deleteClarCom"
                  id={`clarCom_del_${comment.clientId}`}
                  className="btn btn-danger ml-2"
                  onClick={handleClick_delete}
                >
                  <FontAwesomeIcon icon="trash-alt" className="fa-fw mr-1" />
                  Del
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
